/**
 * Navigator Tuning · Plan detail layout (feature 130): header, Best Match card and the
 * per-specialty provider list, shared by the read-only and the tunable plan views.
 */
import { useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { Star } from 'lucide-react';
import { Tabs, TabsTrigger } from '@/shared/components/ui/tabs';
import { ScrollableTabsList } from '@/shared/components/ui/scrollable-tabs-list';
import { BestMatchBreakdown, ProviderCard, type Row } from './PlanProviderCard';
import { pts } from './parameters';
import type { PlanDetail, PlanSearch } from '../types/navigator-tuning.type';

const PLAN_HISTORY_PATH = '/admin/navigator-tuning/plan-history';

const USER_TYPE_LABEL = {
  fp: 'FP',
  guest: 'Guest',
  seeker: 'Seeker',
  seeker_ex_guest: 'Seeker (ex-guest)',
};

export function PlanLayout({
  plan,
  rowsOf,
  bestMatchId,
  bestMatchName = plan.bestMatch?.name,
  was,
  aside,
  toolbar,
  children,
}: {
  plan: PlanDetail;
  rowsOf: (search: PlanSearch) => Row[];
  bestMatchId?: string;
  bestMatchName?: string;
  was?: string;
  aside?: ReactNode;
  toolbar?: ReactNode;
  children: ReactNode;
}) {
  const [active, setActive] = useState(0);
  const search = plan.searches[active];
  const rows = search ? rowsOf(search) : [];
  const era = plan.tunable ? 'C' : plan.era;
  const summary = plan.searches.find((s) => s.inputQuerySource === 'summary')?.inputQuery;
  const version = plan.configVersion
    ? `config v${plan.configVersion.versionNumber}`
    : 'Legacy config';
  // Highest-scored row of the Best Match across the plan's specialties, in the shown view.
  const bestRow = plan.searches
    .flatMap((s) => rowsOf(s).map((r) => ({ ...r, specialty: s.specialty.name })))
    .filter((r) => r.providerId === bestMatchId && r.rank !== null)
    .sort((a, b) => (b.score ?? 0) - (a.score ?? 0))[0];

  return (
    <div className="space-y-6 bg-background">
      <header className="flex flex-wrap items-center justify-between gap-2">
        <nav aria-label="Breadcrumb" className="flex items-center gap-2 text-sm text-[#56607a]">
          <Link to={PLAN_HISTORY_PATH} className="font-semibold text-bquest-green">
            Plan history
          </Link>
          <span aria-hidden="true">/</span>
          <span className="font-mono text-[#1b2232]">{plan.sessionId.slice(0, 8)}</span>
        </nav>
        <span className="flex flex-wrap items-center gap-2 text-sm text-[#56607a]">
          <span className="rounded-full bg-[#f1ede6] px-2.5 py-1 text-xs font-semibold text-[#56607a]">
            {USER_TYPE_LABEL[plan.userType]}
          </span>
          {[
            plan.careStage?.name,
            plan.state,
            plan.county,
            new Date(plan.createdAt).toLocaleString('en-US'),
            `generated with ${version}`,
          ]
            .filter(Boolean)
            .join(' · ')}
        </span>
      </header>
      <div
        className={`grid items-start gap-6 ${aside ? 'lg:grid-cols-[minmax(0,1fr)_400px]' : ''}`}
      >
        <section aria-label="Plan result" className="min-w-0 space-y-4">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <h2 className="font-recoleta text-[26px] font-semibold text-[#1b2232]">Plan result</h2>
            {toolbar}
          </div>
          {children}
          {(plan.careStage || summary) && (
            <div className="flex flex-col gap-1.5 rounded-[14px] border border-border-subtle bg-white px-5 py-[18px]">
              <span className="text-xs font-semibold uppercase tracking-wider text-[#56607a]">
                Care stage
              </span>
              {plan.careStage && (
                <span className="font-recoleta text-xl font-semibold text-[#1b2232]">
                  {plan.careStage.name}
                </span>
              )}
              {summary && <p className="text-[#56607a]">{summary}</p>}
            </div>
          )}
          <section
            aria-label="Best Match"
            className="flex flex-wrap items-center gap-4 rounded-[14px] border border-[#ecd9a8] bg-[#fffaf0] px-5 py-[18px]"
          >
            <span className="flex h-11 w-11 flex-none items-center justify-center rounded-full bg-[#b7791f] text-white">
              <Star aria-hidden="true" className="h-[22px] w-[22px]" />
            </span>
            <div className="flex flex-1 basis-[260px] flex-col gap-0.5">
              <span className="text-xs font-bold uppercase tracking-wider text-[#7a4f0e]">
                Best Match · plan-wide
              </span>
              <span className="text-lg font-bold text-[#1b2232]">{bestMatchName ?? 'None'}</span>
              {bestRow && (
                <span className="text-[#56607a]">
                  {bestRow.specialty}
                  {era === 'C' && bestRow.score !== null && ` · score ${pts(bestRow.score)} pts`}
                </span>
              )}
            </div>
            {was && (
              <span className="rounded-lg bg-[#1b2232] px-2.5 py-1.5 text-[13px] text-white">
                Was: {was}
              </span>
            )}
            {era === 'C' && bestRow && <BestMatchBreakdown breakdown={bestRow.breakdown} />}
          </section>
          <div className="overflow-hidden rounded-[14px] border border-border-subtle bg-white">
            <Tabs value={String(active)} onValueChange={(v) => setActive(Number(v))}>
              {/* White fade under the scroll arrows, matching the card instead of the page cream. */}
              <ScrollableTabsList
                aria-label="Specialties"
                containerClassName="[--background:0_0%_100%]"
                className="h-auto w-full justify-start rounded-none border-b border-border-subtle bg-transparent p-0"
              >
                {plan.searches.map((s, i) => {
                  const changes = rowsOf(s).filter(
                    (r) => r.baselineRank !== undefined && r.baselineRank !== r.rank
                  ).length;
                  return (
                    <TabsTrigger
                      key={s.searchId}
                      value={String(i)}
                      className="min-h-12 rounded-none px-4 text-sm font-medium text-[#56607a] data-[state=active]:font-bold data-[state=active]:text-[#014d41] data-[state=active]:shadow-[inset_0_-2px_0_#026857]"
                    >
                      {s.specialty.name}
                      {changes > 0 && (
                        <span
                          aria-hidden="true"
                          title={`${changes} rank changes`}
                          className="ml-1.5 rounded-full bg-[#fdecdc] px-[7px] py-px text-[11px] font-bold text-[#9a3412]"
                        >
                          {changes}
                        </span>
                      )}
                    </TabsTrigger>
                  );
                })}
              </ScrollableTabsList>
            </Tabs>
            {search && (
              <>
                <div className="flex flex-col gap-1 border-b border-[#efeae1] bg-[#fcfbf8] px-5 py-4">
                  <span className="font-bold text-[#1b2232]">{search.specialty.name}</span>
                  <span className="text-[#56607a]">
                    {search.specialty.rankOrder === 1
                      ? 'Primary specialty'
                      : `Rank ${search.specialty.rankOrder}`}{' '}
                    · {search.status}
                  </span>
                </div>
                {rows.length ? (
                  <ol>
                    {rows.map((row) => {
                      const recorded = search.results.find((r) => r.providerId === row.providerId);
                      return (
                        <ProviderCard
                          key={row.providerId}
                          row={row}
                          era={era}
                          bestMatchId={bestMatchId}
                          flags={recorded}
                        />
                      );
                    })}
                  </ol>
                ) : (
                  <p className="px-5 py-4 text-sm text-[#56607a]">
                    No providers in this specialty.
                  </p>
                )}
              </>
            )}
          </div>
          <p className="text-[13px] text-[#56607a]">
            Sandbox preview. The user’s delivered plan is never changed; it stays on the config it
            was generated with.
          </p>
        </section>
        {aside}
      </div>
    </div>
  );
}
